import apiService from './api'

export interface UserProfile {
  _id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  avatar?: string
  role: string
  schoolId: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface SchoolProfile {
  _id: string
  name: string
  slug: string
  description: string
  shortDescription?: string
  location: string
  country: string
  city: string
  address: string
  coordinates?: {
    lat: number
    lng: number
  }
  phone: string
  email: string
  website?: string
  price: number
  currency: string
  duration: number
  maxStudents: number
  rating: number
  totalReviews: number
  isFeatured: boolean
  isActive: boolean
  isVerified?: boolean
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED'
  images: {
    logo?: string
    coverImage?: string
    gallery: string[]
  }
  videos: {
    introVideo?: string
    gallery: string[]
  }
  programs: {
    teacherTraining: boolean
    workshops: boolean
    retreats: boolean
    onlineClasses: boolean
  }
  facilities: string[]
  amenities: string[]
  certifications: string[]
  features: string[]
  bankDetails?: {
    bankName?: string
    accountNumber?: string
    accountHolderName?: string
    ifscCode?: string
    branchName?: string
  }
  paypalDetails?: {
    paypalEmail?: string
  }
  howToReach?: {
    stations: Array<{
      name: string
      options: Array<{ mode: 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'; details?: string }>
    }>
  }
  createdAt: string
  updatedAt: string
}

class ProfileService {
  /**
   * Get current user profile
   */
  async getUserProfile(): Promise<{ success: boolean; data: UserProfile }> {
    try {
      const response = await apiService.getUserProfile()
      return response
    } catch (error) {
      console.error('Error fetching user profile:', error)
      throw error
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(data: Partial<UserProfile>): Promise<{ success: boolean; data: UserProfile }> {
    try {
      const response = await apiService.updateUserProfile(data)
      return response
    } catch (error) {
      console.error('Error updating user profile:', error)
      throw error
    }
  }

  /**
   * Get school profile
   */
  async getSchoolProfile(): Promise<{ success: boolean; data: SchoolProfile }> {
    try {
      const response = await apiService.getSchoolProfile()
      return response
    } catch (error) {
      console.error('Error fetching school profile:', error)
      throw error
    }
  }

  /**
   * Update school profile
   */
  async updateSchoolProfile(data: Partial<SchoolProfile>): Promise<{ success: boolean; data: SchoolProfile }> {
    try {
      const response = await apiService.updateSchoolProfile(data)
      return response
    } catch (error) {
      console.error('Error updating school profile:', error)
      throw error
    }
  }

  /**
   * Upload school image
   */
  async uploadSchoolImage(file: File, type: 'logo' | 'cover' | 'gallery'): Promise<{ success: boolean; data: { url: string } }> {
    try {
      const formData = new FormData()
      formData.append('image', file)
      formData.append('type', type)

      const response = await apiService.post('/schools/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response
    } catch (error) {
      console.error('Error uploading school image:', error)
      throw error
    }
  }

  /**
   * Upload school video
   */
  async uploadSchoolVideo(file: File, type: 'intro' | 'gallery'): Promise<{ success: boolean; data: { url: string } }> {
    try {
      const formData = new FormData()
      formData.append('video', file)
      formData.append('type', type)

      const response = await apiService.post('/schools/upload-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response
    } catch (error) {
      console.error('Error uploading school video:', error)
      throw error
    }
  }

  /**
   * Delete school image
   */
  async deleteSchoolImage(imageUrl: string): Promise<{ success: boolean }> {
    try {
      const response = await apiService.delete('/schools/images', {
        data: { imageUrl }
      })
      return response
    } catch (error) {
      console.error('Error deleting school image:', error)
      throw error
    }
  }

  /**
   * Delete school video
   */
  async deleteSchoolVideo(videoUrl: string): Promise<{ success: boolean }> {
    try {
      const response = await apiService.delete('/schools/videos', {
        data: { videoUrl }
      })
      return response
    } catch (error) {
      console.error('Error deleting school video:', error)
      throw error
    }
  }

  /**
   * Change user password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.changePassword(currentPassword, newPassword)
      return response
    } catch (error) {
      console.error('Error changing password:', error)
      throw error
    }
  }

  /**
   * Get school verification status
   */
  async getVerificationStatus(): Promise<{
    success: boolean
    data: {
      status: string
      documents: Array<{ url: string; type: string }>
      verificationMeta?: Record<string, string>
      createdBySource?: 'YOGA_SCHOOL' | 'BOOKMYYOGATTC'
    }
  }> {
    try {
      const response = await apiService.get('/schools/verification-status')
      return response
    } catch (error) {
      console.error('Error fetching verification status:', error)
      throw error
    }
  }

  /**
   * Get presigned URL for direct upload of verification document to R2/Cloudflare.
   * Call this when user selects a file; then upload file to the returned uploadUrl via PUT.
   */
  async getVerificationDocumentUploadUrl(
    fileName: string,
    fileType: string,
    fileSize: number
  ): Promise<{ success: boolean; data: { uploadUrl: string; fileKey: string; cloudFrontUrl: string } }> {
    try {
      const response = await apiService.post('/schools/verification-upload-url', {
        fileName,
        fileType,
        fileSize,
      })
      return response
    } catch (error) {
      console.error('Error getting verification upload URL:', error)
      throw error
    }
  }

  /**
   * Submit verification documents by file keys (already uploaded to R2).
   * Types: businessReg, gst, tradeLicense, ownerId, addressProof, yogaAlliance, additional
   */
  async submitVerificationDocumentsByKeys(
    fileKeys: string[],
    documentTypes?: string[],
    verificationMeta?: Record<string, string>
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post('/schools/verification-documents-by-keys', {
        fileKeys,
        documentTypes,
        verificationMeta,
      })
      return response
    } catch (error) {
      console.error('Error submitting verification documents by keys:', error)
      throw error
    }
  }

  /**
   * Update verification meta only (GST, RYS numbers, etc.) without uploading new files.
   */
  async updateSchoolVerificationMeta(meta: Record<string, string>): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiService.updateSchoolProfile({ verificationMeta: meta })
      return response
    } catch (error) {
      console.error('Error updating verification meta:', error)
      throw error
    }
  }

  /**
   * Submit verification documents with optional metadata and document types (same order as documents).
   * Types: businessReg, gst, tradeLicense, ownerId, addressProof, yogaAlliance, additional
   */
  async submitVerificationDocuments(
    documents: File[],
    meta?: Record<string, string>,
    documentTypes?: string[]
  ): Promise<{ success: boolean; message: string }> {
    try {
      const formData = new FormData()
      documents.forEach((doc) => {
        formData.append('documents', doc)
      })

      if (meta) {
        formData.append('verificationMeta', JSON.stringify(meta))
      }

      const types = documentTypes && documentTypes.length === documents.length
        ? documentTypes
        : documents.map(() => 'additional')
      formData.append('documentTypes', JSON.stringify(types))

      const response = await apiService.post('/schools/verification-documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      })
      return response
    } catch (error) {
      console.error('Error submitting verification documents:', error)
      throw error
    }
  }
}

export default new ProfileService()






